<template>
  <VDataGridV2
    id="production-plan-registry"
    :loader="dataLoader"
    :context-menu="ProductionPlansService.contextMenu"
    sticky
    show-action-bar
  >
    <VDataGridColumnV2
      id="productionSite"
      width="150px"
      label="Площадка"
      content-class="ellipsis"
      filterable
      filter-type="select"
      :filter-props="{
      	placeholder: 'Площадка',
				clearable: true,
				options: this.productionSites
      }"
      filter-send-name="productionSite"
      content-align="center"
      filter-identity="*"
    >
      <template #default="{ row }">
        {{ row.productionSite?.name || '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="productCode"
      width="100px"
      filterable
      :filter-props="{
      	placeholder: 'Код',
				clearable: true,
				searchable: true
      }"
      content-align="center"
    >
      <template #label>
        Код
      </template>
      <template #default="{ row }">
        {{ row.code }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="productName"
      width="400px"
      label="Наименование"
      filterable
      :filter-props="{
      	placeholder: 'Наименование продукта',
				clearable: true,
				searchable: true
      }"
      content-align="left"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.name"
        >
          {{ row.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-for="monthKey in monthKeys"
      :id="'month_' + monthKey"
      :key="monthKey"
      width="50px"
      content-align="right"
    >
      <template #label>
        <VButton
          theme="link"
          @contextmenu.prevent="openHeaderGridPopupMenu($event, monthKey)"
        >
          <div
            class="date-wrap-btn"
            style="text-align: right; color: var(--base-strong)"
          >
            <div>{{ ProductionPlansService.formatMonthLabel(monthKey).month }}</div>
            <div>{{ ProductionPlansService.formatMonthLabel(monthKey).year }}</div>
          </div>
        </VButton>
      </template>
      <template #default="{ row }">
        <div
          :class="{
            'low-qty': row.qtyByDates[monthKey]?.qty.isEmpty() || row.qtyByDates[monthKey]?.qty.lessThan(1.001)
          }"
          class="clickable-cell"
          @click="openQtyPopup($event, row, monthKey)"
        >
          {{ row.qtyByDates[monthKey]?.qty?.formatSmart() ?? '-' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2>
      <VButton
        @click="showImportDialog(false)"
      >
        <template #icon>
          <VIcon
            material="cloud_sync"
            theme="outlined"
          />
        </template>
        Загрузить изменения планов
      </VButton>

      <VButton
        @click="showImportDialog(true)"
      >
        <template #icon>
          <VIcon
            material="cloud_upload"
            theme="outlined"
          />
        </template>
        Загрузить производственный план
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>

  <ProductionPlansChangeQtuPopup
    :target="changeQtyPopup.target"
    :month-key="changeQtyPopup.monthKey"
    :row="changeQtyPopup.row"
    @show-product-cell-history="showProductCellHistoryDialog(changeQtyPopup.row, changeQtyPopup.monthKey)"
    @close="closeChangeQtyPopup"
  />

  <ProductionPlanGridPopupMenu
    :visible="headerMenu.visible"
    :target="headerMenu.target"
    :plans="headerMenu.plans"
    :month-key="headerMenu.monthKey"
    @update:visible="headerMenu.visible = $event"
    @add-position="showAddPositionPopup(monthKeys)"
    @show-history="showHistory"
  />
</template>

<style lang="scss" src="./ProductionPlansRegistry.scss"/>

<script lang="ts">

import DataGridLoader 								from '@/lib/components/VDataGridV2/DataGridLoader';
import ProductionSiteService 					from "@/domains/ProductionSites/ProductionSitesService";
import ProductionPlansService         from "@/domains/ProductionPlans/ProductionPlansService";

import ProductionPlanGridPopupMenu    from "./ProductionPlansGridPopupMenu/ProductionPlanGridPopupMenu.vue";
import ProductionPlansChangeQtuPopup  from "./ProductionPlansChangeQtuPopup/ProductionPlansChangeQtuPopup.vue";

export default defineComponent({
  name:       'ProductionPlansRegistry',
  components: {
    ProductionPlanGridPopupMenu,
    ProductionPlansChangeQtuPopup,
  },

  data() {
    return {
      ProductionPlansService,

      changeQtyPopup: {
        target:   null,
        row:      null,
        monthKey: null,
      },

      headerMenu: {
        visible: false,
        target: null,
        monthKey: null,
        plans: null,
      },

      /** вся таблица это набор планов, площадка + месяц = 1 план,
       * нет 1 запроса, которым можно было бы получить всю историю изменений,
       * поэтому получаем id всех планов и используем их
       * */
      currentPlansIds: [],
      productionSites: [],

      dataLoader: new DataGridLoader(ProductionPlansService.getListPaginated),
    };
  },

  computed: {
    positions() {
      return this.dataLoader.data;
    },

    monthKeys() {
      if (!this.$core.utils.isNotEmptyArray(this.positions)) {
        return [];
      }

      /** если position[0] не содержит нужных нам ключей,
       * то выведет только месяцы из первой позиции,
       *
       * поэтому берем все, TODO переорганизовать это лучше */

      const monthKeysSet = new Set();

      this.positions.forEach(pos => {
        Object.keys(pos.qtyByDates)
          .forEach(key => {
            monthKeysSet.add(key);
          });
      });

      return Array.from(monthKeysSet)
        .sort();
    },
  },

  created() {
    this.loadProductionSites();
    this.loadActualPlans();
  },

  methods: {
    async showImportDialog(isPlan) {
      const isUploaded = await this.$core.dialogs.ProductionPlansImportDialog.showAsPromise({ isPlan });

      if (isUploaded) {
        this.renewData(true);
      }
    },

    async loadProductionSites() {

      /**
       * попросили костыль вколотить по этой задаче https://trello.com/c/Wq7zfP19/7910
       * "finsib" "sw_berdsk_research"
       */

      const sites = await ProductionSiteService.getList(this.$core.currentPartner, null);
      this.productionSites = sites.filter(_site => _site.id !== 'finsib' && _site.id !== "sw_berdsk_research")
    },

    async loadActualPlans() {
      this.currentPlansIds = await ProductionPlansService.getActualPlans();
    },

    renewData(isForce = false) {
      if (isForce) this.$emit('change');
      this.dataLoader.refresh();
    },

    openQtyPopup(event, row, monthKey) {
      if (this.changeQtyPopup.target === event.target) {
        this.closeChangeQtyPopup();
        return;
      }

      this.changeQtyPopup.target = event.target;

      this.changeQtyPopup.row      = row;
      this.changeQtyPopup.monthKey = monthKey;
    },

    closeChangeQtyPopup() {
      this.changeQtyPopup.target = null;
    },

    openHeaderGridPopupMenu(event, monthKey) {
      const target = event.currentTarget;

      if (this.headerMenu.visible && this.headerMenu.target === target) {
        this.headerMenu.visible = false;
        return;
      }

      this.headerMenu.plans    = this.currentPlansIds[monthKey] ?? [];
      this.headerMenu.target   = target;
      this.headerMenu.monthKey = monthKey;
      this.headerMenu.visible  = true;
    },

    async showAddPositionPopup(monthKeys) {
      this.headerMenu.visible = false;

      const isAdded = await this.$core.dialogs.AddProductionPositionPopup.showAsPromise({ monthKeys });

      if (isAdded) {
        this.renewData(true);
      }
    },

    showHistory(plan) {
      this.headerMenu.visible = false;
      const date = ProductionPlansService.formatMonthLabel(this.headerMenu.monthKey);
      /** точка в Производство. Бердск ломает форматирование и для файла пропадает расширение */
      const cleanSite = plan.site.replace(/\./g, "");

      this.$core.dialogs.FeedDialog.show(
        {
          entityTypeName: 'productionPlan',
          entityId: plan.id,
          showAddComment: false,
          title: `${date.month} ${date.year} ${cleanSite}`,
          showDownloadBtn: true,
        },
      );
    },

    /** выводим историю */

    showProductCellHistoryDialog(row, monthKey) {
      this.$core.dialogs.FeedDialog.show(
        {
          entityTypeName: 'productionPlanProduct',
          showAddComment: false,
          showDownloadBtn: true,
          title: `${row.name}`,
          productCode: row.code,
          plannedArrivalDate: monthKey,
        },
      );
    },
  }
});
</script>
